/**
 * Leaderboard — Top Players by lifetime XRP winnings.
 *
 * Ranks players by total `winnings` transactions, side-by-side with their win
 * count and unique badge count. First three ranks get gold/silver/bronze
 * accents; the current signed-in user's row is highlighted if present.
 * Public page — no auth required.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, Trophy, Award, Crown, Medal, RotateCcw, Coins, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';

const tg = () => window.Telegram?.WebApp;

const RANK_STYLE = {
  1: { icon: Crown, ring: 'ring-2 ring-amber-400', badge: 'bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900', label: 'text-amber-300' },
  2: { icon: Medal, ring: 'ring-1 ring-slate-300/40', badge: 'bg-gradient-to-br from-slate-200 to-slate-400 text-slate-900', label: 'text-slate-200' },
  3: { icon: Award, ring: 'ring-1 ring-orange-400/40', badge: 'bg-gradient-to-br from-orange-400 to-amber-600 text-slate-900', label: 'text-orange-300' },
};

const initials = (name) => (name || '?').trim().slice(0, 2).toUpperCase();

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await axios.get(`${API}/leaderboard/players?limit=25`);
      setPlayers(r.data.players || []);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }
    return () => { if (tg()) tg().BackButton.hide(); };
  }, []);

  const myUserId = user?.user_id;

  return (
    <div className="min-h-screen pb-10" data-testid="leaderboard-page">
      <header className="sticky top-0 z-40 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="leaderboard-back" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-bold text-white leading-tight">Top Players</h1>
              <p className="text-[11px] text-slate-500">Lifetime XRP winnings leaderboard</p>
            </div>
          </div>
          <Button data-testid="leaderboard-refresh" onClick={fetchLeaderboard} size="sm" variant="ghost" className="text-slate-400 h-8 px-2">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Hero banner */}
      <section className="px-4 pt-4 pb-3">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-red-500/5 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <Trophy className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white leading-tight">Chase the crown</p>
            <p className="text-[11px] text-slate-400 leading-tight mt-0.5">
              Every Fireball match adds to your lifetime XRP total. Ranks update the moment a draw settles.
            </p>
          </div>
        </div>
      </section>

      {/* Rankings */}
      <section className="px-4 pb-4">
        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
          </div>
        ) : error ? (
          <div data-testid="leaderboard-error" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-center">
            <p className="text-sm text-red-300 font-semibold">Couldn't load the leaderboard</p>
            <p className="text-[11px] text-slate-500 mt-1">Check your connection and try again.</p>
            <Button data-testid="leaderboard-retry" onClick={fetchLeaderboard} className="mt-4 bg-slate-800 hover:bg-slate-700 text-slate-200 h-9 text-xs font-bold">
              Retry
            </Button>
          </div>
        ) : players.length === 0 ? (
          <div data-testid="leaderboard-empty" className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 text-center">
            <Target className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-300 font-semibold">No winners yet</p>
            <p className="text-[11px] text-slate-500 mt-1">Be the first — pick a slot on Home and match the Fireball digit.</p>
            <Button data-testid="leaderboard-play-cta" onClick={() => navigate('/')} className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900 h-9 text-xs font-bold">
              Play now
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {players.map((p, idx) => {
              const rank = idx + 1;
              const style = RANK_STYLE[rank];
              const RankIcon = style?.icon;
              const isMe = myUserId && p.user_id === myUserId;
              return (
                <button
                  key={p.user_id}
                  data-testid={`leaderboard-row-${rank}`}
                  onClick={() => navigate(`/profile/${encodeURIComponent(p.username)}`)}
                  className={`w-full flex items-center gap-3 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 active:scale-[0.99] transition-all p-3 text-left ${isMe ? 'ring-2 ring-emerald-400/60' : ''}`}
                >
                  {/* Rank badge */}
                  <div className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center font-black text-sm ${style ? style.badge : 'bg-slate-800 text-slate-300'}`}>
                    {RankIcon ? <RankIcon className="w-4 h-4" strokeWidth={2.75} /> : `#${rank}`}
                  </div>

                  {/* Avatar + name */}
                  <div className={`w-10 h-10 flex-shrink-0 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-300 ${style?.ring || ''}`}>
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.username} className="w-full h-full object-cover" />
                    ) : (
                      initials(p.username)
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm font-bold truncate ${style?.label || 'text-white'}`}>
                        {p.username}
                        {isMe && <span className="ml-1.5 text-[10px] text-emerald-300 font-semibold">· you</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 whitespace-nowrap overflow-hidden">
                      <span className="inline-flex items-center gap-1 flex-shrink-0">
                        <Trophy className="w-3 h-3 text-amber-400" /> {p.wins} win{p.wins === 1 ? '' : 's'}
                      </span>
                      {p.badges > 0 && (
                        <span className="inline-flex items-center gap-1 flex-shrink-0">
                          <Award className="w-3 h-3 text-purple-400" /> {p.badges}
                        </span>
                      )}
                      {p.location && (
                        <span className="text-slate-600 truncate">· {p.location}</span>
                      )}
                    </div>
                  </div>

                  {/* Winnings */}
                  <div className="flex-shrink-0 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Coins className="w-3.5 h-3.5 text-emerald-400" />
                      <span data-testid={`leaderboard-winnings-${rank}`} className="text-sm font-black text-emerald-300 tabular-nums">
                        {(p.lifetime_winnings ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">XRP</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {!loading && players.length > 0 && (
        <p className="text-center text-[10px] text-slate-500 mt-2 px-6">
          Rankings pull from completed draws. Sell-back / refunded games don't count.
        </p>
      )}
    </div>
  );
}
